import { useCallback, useEffect, useState } from 'react';
import { Drawer, Flex, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { ModuleType } from 'infrastructure/enums';
import { useGetKeyNote, useSaveKeyNote } from 'infrastructure/hooks/key-notes';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import KeyNotesContent from 'components/organisms/admin-panel-key-notes/key-notes-content';

import { KeyNoteDrawerHeader } from './header';
import { TabsControl } from './tabs-control';
import { NoteComposer } from './note-composer';
import styles from './styles.module.scss';

import type { IKeyNoteDrawerProps } from './types';

export const KeyNotesDrawer = ({
  open,
  onClose,
  patientGuid,
  patientInfo,
  onNoteSaved,
  isCCM = true,
  isRPM = true,
}: IKeyNoteDrawerProps) => {
  const { t } = useTranslation();
  const { isReadOnlyRole } = useCheckRoleAccess();

  const hasValidModule = isRPM || isCCM;
  const normalizedSupportsRPM = hasValidModule ? isRPM : true;
  const normalizedSupportsCCM = hasValidModule ? isCCM : true;

  const availableModules = [
    normalizedSupportsRPM ? ModuleType.RPM : null,
    normalizedSupportsCCM ? ModuleType.CCM : null,
  ].filter((module): module is ModuleType => Boolean(module));

  const defaultModule = availableModules[0] ?? ModuleType.RPM;

  const [activeModule, setActiveModule] = useState<ModuleType>(defaultModule);
  const [showExpired, setShowExpired] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  const { saveNote, isLoading: isSaving } = useSaveKeyNote({
    module: activeModule,
  });

  const canChangeModule = availableModules.length > 1;

  const [rpmQuery, ccmQuery] = useGetKeyNote({
    module: activeModule,
    patientGuid: patientGuid ?? undefined,
    canChangeModule,
    showExpired,
    enableRPM: normalizedSupportsRPM,
    enableCCM: normalizedSupportsCCM,
  });

  const currentQuery = activeModule === ModuleType.RPM ? rpmQuery : ccmQuery;
  const keyNotesData = currentQuery.data;
  const isLoading = currentQuery.isFetching;

  const handleModuleChange = useCallback(
    (module: ModuleType) => {
      if (!availableModules.includes(module)) return;
      setActiveModule(module);
    },
    [availableModules],
  );

  useEffect(() => {
    if (!availableModules.includes(activeModule)) {
      setActiveModule(availableModules[0] ?? ModuleType.RPM);
    }
  }, [activeModule, availableModules]);

  const handleShowExpiredChange = useCallback((checked: boolean) => {
    setShowExpired(checked);
  }, []);

  const handleAddClick = useCallback(() => {
    setShowComposer(true);
  }, []);

  const handleComposerCancel = useCallback(() => {
    setShowComposer(false);
  }, []);

  const handleSave = useCallback(
    (values: { note: string; expiresAt?: Date | null; module: ModuleType }) => {
      if (!patientGuid) return;

      saveNote(
        {
          patientGuid,
          module: values.module,
          note: values.note,
          expiresAt: values.expiresAt || null,
          showExpired,
        },
        {
          onSuccess() {
            setShowComposer(false);
            setActiveModule(values.module);
            notificationController.success({
              message: t('messages.keynoteAdded'),
            });
            onNoteSaved?.();
          },
        },
      );
    },
    [patientGuid, showExpired, saveNote, t, onNoteSaved],
  );

  if (!patientInfo) return null;

  const patientName = `${patientInfo.firstName} ${patientInfo.lastName}`;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={544}
        closable={false}
        zIndex={1010}
        className={styles.drawer}
      >
        <KeyNoteDrawerHeader
          patientName={patientName}
          birthDate={patientInfo.birthDate}
          language={patientInfo.language}
          doctorName={patientInfo.doctorName}
          onClose={onClose}
        />
        <Flex vertical className={styles['drawer-body']}>
          <div className={styles['drawer-inner-wrapper']}>
            <TabsControl
              activeModule={activeModule}
              onModuleChange={handleModuleChange}
              showExpired={showExpired}
              onShowExpiredChange={handleShowExpiredChange}
              onAddClick={handleAddClick}
              isReadOnly={isReadOnlyRole}
              isLoading={isSaving}
              hideAddButton={showComposer}
              availableModules={availableModules}
            />
          </div>

          <AnimatePresence>
            {showComposer && (
              <NoteComposer
                key="note-composer"
                initialModule={activeModule}
                onSave={handleSave}
                onCancel={handleComposerCancel}
                onModuleChange={handleModuleChange}
                isLoading={isSaving}
                availableModules={availableModules}
              />
            )}
          </AnimatePresence>

          <Flex vertical className={styles['content-section']}>
            {isLoading ? (
              <Flex vertical gap={16} className={styles['note-skeleton']}>
                {[1, 2, 3, 4].map((key) => (
                  <Skeleton
                    key={key}
                    active
                    avatar={false}
                    paragraph={{ rows: 3 }}
                  />
                ))}
              </Flex>
            ) : (
              <KeyNotesContent
                module={activeModule}
                showExpired={showExpired}
                isDataEmpty={!keyNotesData?.length}
                data={keyNotesData}
                patientGuid={patientGuid ?? ''}
                onAction={onNoteSaved}
              />
            )}
          </Flex>
        </Flex>
      </Drawer>
    </>
  );
};
